import React from 'react';
import { Card, Spinner, Text, Badge } from '../../components/UI';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAtomValue } from 'jotai';
import { useProcessingQueue, IProcessingQueueItem, useLogin } from '../../services';
import { processingQueueLoadingAtom } from '../../services/processing_queue/types';
import { IApplyPatternRequest, ICreateDesignRequest, ICreateSampleRequest, ICreateHeaderRequest } from '../../services/product';

type BadgeColor = 'zinc' | 'red' | 'green' | 'yellow';

const statusBadgeColor = (status: string): BadgeColor => {
    switch (status) {
        case 'failed': return 'red';
        case 'processed': return 'green';
        case 'processing': return 'yellow';
        case 'unprocessed': return 'zinc';
        default: return 'zinc';
    }
};

const categoryLabel = (category: string): string => {
    switch (category) {
        case 'MOCKUP_UPLOAD': return 'Mockup Creation';
        case 'PRODUCT_UPLOAD': return 'Product';
        case 'SAMPLE_CREATION': return 'Sample Creation';
        case 'HEADER_CREATION': return 'Header Creation';
        default: return category;
    }
};

// #region getNotificationInfo
const getNotificationInfo = (item: IProcessingQueueItem): string => {
    switch (item.category) {
        case 'MOCKUP_UPLOAD': {
            const passthrough = item.passthrough_data as IApplyPatternRequest;
            return `Store Product ID - ${passthrough.product_id}`;
        }
        case 'PRODUCT_UPLOAD': {
            const passthrough = item.passthrough_data as ICreateDesignRequest;
            return `Store Product ID - ${passthrough.product_id}`;
        }
        case 'SAMPLE_CREATION': {
            const passthrough = item.passthrough_data as ICreateSampleRequest;
            return `Design ID - ${passthrough.design_id}`;
        }
        case 'HEADER_CREATION': {
            const passthrough = item.passthrough_data as ICreateHeaderRequest;
            return `Mockup ID - ${passthrough.generated_mockup_id}`;
        }
        default:
            return '';
    }
};

// #region NotificationsPanel
export const NotificationsPanel: React.FunctionComponent = (): React.ReactElement => {
    const [user] = useLogin();
    const [queue, , queueFunctions] = useProcessingQueue();
    const loading = useAtomValue(processingQueueLoadingAtom);

    React.useEffect(() => {
        if (user?.brand_id) {
            queueFunctions.getQueue(user.brand_id as number);
        }
    }, []);

    const handleRefresh = () => {
        if (user?.brand_id) {
            queueFunctions.getQueue(user.brand_id as number);
        }
    };

    const sortedQueue = [...queue].sort((a, b) => b.id - a.id);

    return (
        <Card
            cardType="description-actions"
            cardTitle="Processing Queue"
            translateTitle={false}
            description="View the status of your processing tasks."
            actions={[
                {
                    buttonAction: handleRefresh,
                    color: 'zinc',
                    content: (<><ArrowPathIcon data-slot="icon" />Refresh</>)
                }
            ]}
        >
            <div className="flex flex-col gap-y-3 py-2">
                {loading && queue.length === 0 ? (
                    <div className="flex justify-center py-8">
                        <Spinner show={true} />
                    </div>
                ) : queue.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-y-2">
                        <Text className="text-gray-500 dark:text-gray-400">No processing tasks to show</Text>
                        <Text className="text-gray-400 dark:text-gray-500 text-sm">Tasks will appear here when you upload mockups or create products</Text>
                    </div>
                ) : (
                    sortedQueue.map((item: IProcessingQueueItem) => (
                        <div
                            key={item.id}
                            className="flex items-start gap-x-4 p-4 rounded-lg border dark:border-stone-700 border-stone-200 dark:bg-stone-800/30 bg-surface"
                        >
                            <div className={`w-1 self-stretch rounded-full shrink-0 ${
                                item.status === 'failed' ? 'bg-red-400' :
                                item.status === 'processed' ? 'bg-green-400' :
                                item.status === 'processing' ? 'bg-yellow-400' :
                                'bg-gray-400'
                            }`} />
                            <div className="flex flex-col gap-y-1 flex-1 min-w-0">
                                <div className="flex items-center gap-x-2 flex-wrap">
                                    <Text className="font-semibold text-sm">
                                        {categoryLabel(item.category)}
                                    </Text>
                                    <Badge color={statusBadgeColor(item.status)}>
                                        {item.status}
                                    </Badge>
                                </div>
                                <Text className="text-xs dark:text-gray-400 text-gray-500">
                                    {getNotificationInfo(item)}
                                </Text>
                                {item.status_message && (
                                    <Text className="text-xs dark:text-gray-400 text-gray-500 mt-1">
                                        {item.status_message}
                                    </Text>
                                )}
                            </div>
                            <Text className="text-xs dark:text-gray-500 text-gray-400 shrink-0">
                                #{item.id}
                            </Text>
                        </div>
                    ))
                )}
                {loading && queue.length > 0 && (
                    <div className="flex justify-center py-2">
                        <Spinner show={true} size={20} />
                    </div>
                )}
            </div>
        </Card>
    );
};
